import type { AssignmentSource } from "@hightouch/lib/llm/lifecycle-studio/dynamic-content/types";
import {
  type ContentEmailEspCampaignLinkQuery,
  useBrandProfileEspDestinationConfigurationQuery,
  useCampaignEspDestinationConfigurationQuery,
  useContentEmailEspCampaignLinkQuery,
  useEspDestinationConfigurationQuery,
} from "src/graphql";
import { resolveEspTarget } from "src/pages/lifecycle-studio/settings/esp-target-selection";
import { resolveInheritedAssignmentSource } from "./resolve-inherited-assignment-source";

export type EspTargetDestination = {
  id: string;
  name: string | null;
  type: string;
};

export type EspTargetResolution = {
  /** Destination id the nearest scope names. Null for Native Delivery or when
   * no scope names anything. */
  targetId: string | null;
  /** The row that id resolves to, when the workspace has one. Null where
   * `targetId` is null, and also where the id names a destination the
   * destinations query doesn't return — it filters on `setup_complete`, so a
   * mid-setup ESP resolves on the backend but not here. */
  destination: EspTargetDestination | null;
  /**
   * Campaign → brand → workspace assignment inherit, then Native Delivery vs
   * ESP. Same walk as `resolveDefaultAssignmentSource`; callers that stamp or
   * display this must not infer it from the destination alone. Email compile
   * stamps are still the caller's job.
   */
  assignmentSource: AssignmentSource;
  isLoading: boolean;
};

const UNRESOLVED_ASSIGNMENT_SOURCE: AssignmentSource = "esp_profile";

/**
 * The distinct campaigns an email is attached to, across both link tables. A
 * unique index holds each table to one row per email, so more than one id here
 * means the two tables disagree — which no email in production currently does.
 * Kept as a guard, not an expected state: the alternative is picking one of two
 * campaigns that can name different ESPs.
 */
function linkedCampaignIds(
  data: ContentEmailEspCampaignLinkQuery | undefined,
): string[] {
  if (!data) return [];
  return [
    ...new Set(
      [...data.lcm_message_candidates, ...data.marketing_campaign_messages].map(
        (row) => String(row.marketing_campaign_id),
      ),
    ),
  ];
}

/**
 * The ESP a campaign (or the workspace) actually sends through: campaign →
 * brand → workspace default, the walk `resolveEmailEspContext` does in
 * `esp-templating/context.ts`. One implementation for every surface: skipping
 * the brand scope reports the wrong provider for a campaign that inherits it.
 *
 * Pass `emailId` from any surface that renders an email, with or without a
 * campaign in hand. Where `campaignId` is absent it is derived from the email's
 * campaign links, so a surface outside a campaign route (the agent chat's focus
 * panel, the standalone message editor) resolves the same target generation and
 * deploy do rather than falling to the workspace default.
 */
export function useEspTarget({
  campaignId,
  emailId,
  brandProfileId,
  enabled = true,
}: {
  campaignId?: string;
  emailId?: string;
  /**
   * The brand scope to walk when there is no campaign to read it from — the
   * intake form names a brand profile before the campaign exists. Ignored
   * once a campaign resolves, which carries its own.
   */
  brandProfileId?: string;
  enabled?: boolean;
}): EspTargetResolution {
  const shouldDeriveCampaign = enabled && !campaignId && Boolean(emailId);
  const linkQuery = useContentEmailEspCampaignLinkQuery(
    { emailId: emailId ?? "" },
    { enabled: shouldDeriveCampaign },
  );
  const linkedIds = linkedCampaignIds(linkQuery.data);
  // More than one campaign has no single ESP to ground on, and the two can name
  // different ones. `resolveEmailEspContext` throws rather than pick; here that
  // is no target, which degrades to the callers' warning.
  const isAmbiguous = linkedIds.length > 1;
  const resolvedCampaignId =
    campaignId ?? (linkedIds.length === 1 ? linkedIds[0] : undefined);

  const workspaceQuery = useEspDestinationConfigurationQuery(undefined, {
    enabled: enabled && !resolvedCampaignId,
  });
  const campaignQuery = useCampaignEspDestinationConfigurationQuery(
    { campaignId: resolvedCampaignId ?? "" },
    { enabled: enabled && Boolean(resolvedCampaignId) },
  );
  const campaign = campaignQuery.data?.marketing_campaigns_by_pk;
  // A campaign's own brand scope wins; the caller's applies only without one.
  const scopeBrandProfileId = resolvedCampaignId
    ? campaign?.brand_profile_id
      ? String(campaign.brand_profile_id)
      : ""
    : (brandProfileId ?? "");
  const brandQuery = useBrandProfileEspDestinationConfigurationQuery(
    { brandProfileId: scopeBrandProfileId },
    { enabled: enabled && Boolean(scopeBrandProfileId) },
  );

  const scopeQuery = resolvedCampaignId ? campaignQuery : workspaceQuery;
  const data = scopeQuery.data;
  // The brand scope sits between campaign and workspace default: resolving
  // before it loads reports the workspace's ESP for a brand-scoped campaign.
  const brandPending = Boolean(scopeBrandProfileId) && !brandQuery.data;
  const linkPending = shouldDeriveCampaign && !linkQuery.data;

  // An unread campaign link resolves to null for the same reason an unread
  // brand scope does, below: the email may well belong to a campaign, so the
  // workspace default is a wrong answer rather than a missing one.
  if (isAmbiguous || (shouldDeriveCampaign && linkQuery.isError)) {
    return {
      targetId: null,
      destination: null,
      assignmentSource: UNRESOLVED_ASSIGNMENT_SOURCE,
      isLoading: false,
    };
  }

  if (linkPending || !data || brandPending) {
    // A failed query never gets data, so missing data alone can't mean "still
    // loading" — holding `isLoading` true strands every surface that waits on
    // the walk. An unread brand scope resolves to null rather than falling
    // through: the workspace's dialect for a brand-scoped campaign is a wrong
    // answer, where null degrades to the callers' warning.
    const isFailed = scopeQuery.isError || (brandPending && brandQuery.isError);
    return {
      targetId: null,
      destination: null,
      assignmentSource: UNRESOLVED_ASSIGNMENT_SOURCE,
      isLoading: enabled && !isFailed,
    };
  }

  const brandProfile = brandQuery.data?.brand_profiles[0];
  const workspaceDefault = data.content_settings[0];
  const workspaceScope = {
    destinationId: workspaceDefault?.default_esp_destination_id,
    targetKind: workspaceDefault?.esp_target_kind,
  };
  const brandScope = {
    destinationId: brandProfile?.esp_destination_id,
    targetKind: brandProfile?.esp_target_kind,
  };
  const { destinationId, destination, isNativeDelivery } = resolveEspTarget({
    scopes: resolvedCampaignId
      ? [
          {
            destinationId: campaign?.esp_destination_id,
            targetKind: campaign?.esp_target_kind,
            // A campaign-level choice we can't honor stops the walk, as it does
            // in `resolveEmailEspContext`.
            stopsOnUnresolved: true,
          },
          brandScope,
          workspaceScope,
        ]
      : scopeBrandProfileId
        ? [brandScope, workspaceScope]
        : [workspaceScope],
    destinations: data.destinations,
  });

  return {
    targetId: destinationId,
    destination: destination
      ? {
          id: String(destination.id),
          name: destination.name ?? null,
          type: destination.type,
        }
      : null,
    assignmentSource: resolveInheritedAssignmentSource({
      campaign:
        resolvedCampaignId && campaign
          ? {
              assignmentSource: campaign.default_assignment_source,
              destinationId: campaign.esp_destination_id,
              targetKind: campaign.esp_target_kind,
            }
          : null,
      brand: brandProfile
        ? {
            assignmentSource: brandProfile.default_assignment_source,
            destinationId: brandProfile.esp_destination_id,
            targetKind: brandProfile.esp_target_kind,
          }
        : null,
      workspaceAssignmentSource: workspaceDefault?.default_assignment_source,
      isNativeDelivery,
    }),
    isLoading: false,
  };
}
